import { Link } from "expo-router"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { createSupportTicketAsync } from "@/base/farmhq-api"
import { ActionButtons } from "@/components/ActionButtons"
import { AuthenticatedOnly } from "@/components/makeFilterComponent"
import { CustomerService } from "@/constants/base"
import { useFormValidation } from "@/hooks/useFormvalidation"
import { useRequestHelpers } from "@/hooks/useRequestHelpers"
import { getActiveUserEmailFromState } from "@/redux/selectors"
import { useRootSelector } from "@/redux/useRootSelector"
import { AppText, Box, FormControl, Row, WordDivider } from "@/theme"
import i18n from "@/translations/i18n"

import { StyleSheet } from "react-native"

export default function CreateSupportTicketForm({
  onClose,
}: {
  onClose: () => void
}): React.JSX.Element {
  const initialEmail = useRootSelector(getActiveUserEmailFromState)
  const { t } = useTranslation("support")
  const { required } = useFormValidation()
  const { dispatch, handleError, isLoading, toasts } = useRequestHelpers(
    "CreateSupportTicket",
  )

  const form = useForm({
    defaultValues: {
      body: "",
      email: initialEmail,
      subject: "",
    },
  })

  const onSubmit = form.handleSubmit((values) => {
    if (typeof values.email === "undefined") {
      throw new TypeError("email is undefined")
    }

    dispatch(
      createSupportTicketAsync({
        body: values.body,
        email: values.email.toLowerCase(),
        subject: values.subject,
      }),
    )
      .unwrap()
      .then(() => toasts.success(t("weHaveReceivedYourMessage")))
      .then(onClose)
      .catch((error) => {
        handleError(error, {
          toastMessage: "default",
        })
      })
  })
  return (
    <FormProvider {...form}>
      {Boolean(initialEmail) ? null : (
        <Controller
          name={"email"}
          rules={{ required }}
          render={({ field: { onChange, ...field }, fieldState }) => {
            const errorMessage = fieldState.error?.message
            const isInvalid = Boolean(errorMessage)
            return (
              <FormControl.Provider
                id={"email"}
                isInvalid={isInvalid}
                mb={"$4"}
              >
                <FormControl.Label>
                  {i18n.t("auth:emailFieldLabel")}
                </FormControl.Label>
                <FormControl.Input
                  {...field}
                  placeholder={i18n.t("auth:emailFieldPlaceholder")}
                  onChangeText={onChange}
                />
                <FormControl.ErrorMessage>
                  {errorMessage}
                </FormControl.ErrorMessage>
              </FormControl.Provider>
            )
          }}
        />
      )}
      <Controller
        control={form.control}
        name={"subject"}
        rules={{ required }}
        render={({ field: { onChange, ref, ...field }, fieldState }) => {
          const errorMessage = fieldState.error?.message
          return (
            <FormControl.Provider
              id={"subject"}
              isInvalid={Boolean(errorMessage)}
            >
              <FormControl.Label>{t("subjectInputLabel")}</FormControl.Label>
              <FormControl.Input
                ref={ref}
                placeholder={t("subjectInputPlaceholder")}
                onChangeText={onChange}
                {...field}
              />
              <FormControl.ErrorMessage>
                {errorMessage}
              </FormControl.ErrorMessage>
            </FormControl.Provider>
          )
        }}
      />
      <Controller
        control={form.control}
        name={"body"}
        rules={{ required }}
        render={({ field: { onChange, ref, ...field }, fieldState }) => {
          const errorMessage = fieldState.error?.message
          return (
            <FormControl.Provider
              id={"body"}
              isInvalid={Boolean(errorMessage)}
              mt={"$4"}
            >
              <FormControl.Label>{t("bodyInputLabel")}</FormControl.Label>
              <FormControl.Input
                ref={ref}
                multiline
                numberOfLines={8}
                placeholder={t("bodyInputPlaceholder")}
                style={styles.input}
                onChangeText={onChange}
                {...field}
              />
              <FormControl.ErrorMessage>
                {errorMessage}
              </FormControl.ErrorMessage>
            </FormControl.Provider>
          )
        }}
      />
      <ActionButtons
        isLoading={isLoading}
        mt={"$4"}
        orientation={"horizontal"}
        onPressCancel={onClose}
        onPressSubmit={onSubmit}
      />
      <AuthenticatedOnly>
        <Box my={"$4"}>
          <WordDivider />
        </Box>
        <Row justifyContent={"space-between"}>
          <AppText>{t("callUs")}</AppText>
          <Box>
            <Link asChild href={`tel:${CustomerService.PHONE_NUMBER}`}>
              <AppText fontWeight={"bold"}>
                {CustomerService.PHONE_NUMBER}
              </AppText>
            </Link>
          </Box>
        </Row>
      </AuthenticatedOnly>
    </FormProvider>
  )
}
const styles = StyleSheet.create({
  input: {
    height: 200,
  },
})
